import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  PawPrint,
  Stethoscope,
  ClipboardList,
  Apple,
  Home,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Users,
  Activity,
  Heart,
  CheckCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Animal, HealthRecord, Task } from '../LivestockManagementPage';

// Import design system tokens
import { 
  getCardClasses, 
  getShadowClasses, 
  colors, 
  typography,
  spacing
} from '@/components/business/design-system/businessDesignTokens';

interface DashboardTabProps {
  stats: {
    totalAnimals: number;
    healthyAnimals: number;
    pendingTasks: number;
    upcomingFeedings: number;
    alerts: number;
  };
  animals: Animal[];
  tasks: Task[];
  healthRecords: HealthRecord[];
  onNavigateToTab: (tab: string) => void;
}

export function DashboardTab({
  stats,
  animals,
  tasks,
  healthRecords,
  onNavigateToTab
}: DashboardTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock' });
  
  // Get animals that need attention
  const animalsNeedingAttention = animals.filter(a => 
    a.status === 'Under Treatment' || a.status === 'Critical' || a.status === 'Quarantine'
  );

  // Get urgent tasks
  const urgentTasks = tasks.filter(t => 
    t.priority === 'Urgent' || t.priority === 'High' && t.status !== 'Completed'
  );

  // Get overdue tasks
  const overdueTasks = tasks.filter(t => {
    const today = new Date();
    const dueDate = new Date(t.dueDate);
    return dueDate < today && t.status !== 'Completed';
  });

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">
          {t('dashboardDescription')}
        </p>
      </div>

      {/* Compact Key Metrics */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-4">
          <div className="flex items-center justify-evenly w-full">
            <div 
              className="flex flex-col items-center text-center cursor-pointer hover:scale-105 transition-transform duration-200 min-w-0 flex-1"
              onClick={() => onNavigateToTab('animals')}
            >
              <div className="flex flex-col items-center mb-2">
                <PawPrint className={`h-5 w-5 text-${colors.success.main} mb-1`} />
                <div className="text-lg font-bold text-slate-900">{stats.totalAnimals}</div>
              </div>
              <div className="text-xs text-slate-600 mb-1">{t('totalAnimals')}</div>
              <div className={`text-xs text-${colors.success.text} leading-tight`}>
                {stats.healthyAnimals} {t('healthy')} • {stats.totalAnimals - stats.healthyAnimals} {t('needsAttention')}
              </div>
            </div>

            <div 
              className="flex flex-col items-center text-center cursor-pointer hover:scale-105 transition-transform duration-200 min-w-0 flex-1"
              onClick={() => onNavigateToTab('health')}
            >
              <div className="flex flex-col items-center mb-2">
                <Heart className="h-5 w-5 text-red-500 mb-1" />
                <div className="text-lg font-bold text-slate-900">{stats.healthyAnimals}</div>
              </div>
              <div className="text-xs text-slate-600 mb-1">{t('healthStatus')}</div>
              <div className="text-xs text-slate-600 leading-tight">
                {((stats.healthyAnimals / stats.totalAnimals) * 100).toFixed(0)}% {t('healthyAnimals')}
              </div>
            </div>

            <div 
              className="flex flex-col items-center text-center cursor-pointer hover:scale-105 transition-transform duration-200 min-w-0 flex-1"
              onClick={() => onNavigateToTab('tasks')}
            >
              <div className="flex flex-col items-center mb-2">
                <ClipboardList className={`h-5 w-5 text-${colors.primary.main} mb-1`} />
                <div className="text-lg font-bold text-slate-900">{stats.pendingTasks}</div>
              </div>
              <div className="text-xs text-slate-600 mb-1">{t('pendingTasks')}</div>
              <div className="text-xs text-slate-600 leading-tight">
                {urgentTasks.length} {t('urgent')}
              </div>
            </div>

            <div className="flex flex-col items-center text-center min-w-0 flex-1">
              <div className="flex flex-col items-center mb-2">
                <Apple className="h-5 w-5 text-orange-500 mb-1" />
                <div className="text-lg font-bold text-slate-900">{stats.upcomingFeedings}</div>
              </div>
              <div className="text-xs text-slate-600 mb-1">{t('todayFeedings')}</div>
              <div className="text-xs text-slate-600 leading-tight">
                {t('scheduledFeedings')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Streamlined Quick Actions */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-slate-900 flex items-center gap-2">
          <Activity className={`h-4 w-4 text-${colors.success.main}`} />
          {t('quickActions')}
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            size="sm"
            className="gap-2 hover:scale-[1.02] transition-all duration-200"
            onClick={() => onNavigateToTab('animals')}
          >
            <PawPrint className={`h-4 w-4 text-${colors.success.main}`} />
            {t('addNewAnimal')}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            className="gap-2 hover:scale-[1.02] transition-all duration-200"
            onClick={() => onNavigateToTab('health')}
          >
            <Stethoscope className="h-4 w-4 text-red-500" />
            {t('recordHealthEvent')}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            className="gap-2 hover:scale-[1.02] transition-all duration-200"
            onClick={() => onNavigateToTab('tasks')}
          >
            <ClipboardList className={`h-4 w-4 text-${colors.primary.main}`} />
            {t('createTask')}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            className="gap-2 hover:scale-[1.02] transition-all duration-200"
            onClick={() => onNavigateToTab('feeding')}
          >
            <Apple className="h-4 w-4 text-orange-500" />
            {t('logFeeding')}
          </Button>
        </div>
      </div>

      {/* Enhanced Alerts and Attention Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Animals Needing Attention */}
        <Card className={getCardClasses('basic')}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className={`h-5 w-5 text-${colors.warning.main}`} />
                <span>{t('animalsNeedingAttention')}</span>
              </div>
              {animalsNeedingAttention.length > 0 && (
                <Badge variant="secondary">{animalsNeedingAttention.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {animalsNeedingAttention.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className={`h-8 w-8 mx-auto mb-2 text-${colors.success.main}`} />
                <p>{t('allAnimalsHealthy')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {animalsNeedingAttention.slice(0, 3).map((animal) => (
                  <div key={animal.id} className={cn(
                    "flex items-center justify-between p-3 rounded-lg",
                    getShadowClasses('level1').replace('hover:', ''),
                    "bg-slate-50"
                  )}>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <PawPrint className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{animal.name}</p>
                        <p className="text-sm text-slate-600">{animal.species}</p>
                      </div>
                    </div>
                    <Badge 
                      variant={animal.status === 'Critical' ? 'destructive' : 'secondary'}
                    >
                      {animal.status}
                    </Badge>
                  </div>
                ))}
                {animalsNeedingAttention.length > 3 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => onNavigateToTab('animals')}
                  >
                    {t('viewAll')} ({animalsNeedingAttention.length})
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Urgent Tasks */}
        <Card className={getCardClasses('basic')}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className={`h-5 w-5 text-${colors.primary.main}`} />
                <span>{t('urgentTasks')}</span>
              </div>
              {urgentTasks.length > 0 && (
                <Badge variant="secondary">{urgentTasks.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {urgentTasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className={`h-8 w-8 mx-auto mb-2 text-${colors.success.main}`} />
                <p>{t('noUrgentTasks')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {urgentTasks.slice(0, 3).map((task) => (
                  <div key={task.id} className={cn(
                    "flex items-center justify-between p-3 rounded-lg",
                    getShadowClasses('level1').replace('hover:', ''),
                    "bg-slate-50"
                  )}>
                    <div>
                      <p className="font-medium text-slate-900">{task.title}</p>
                      <p className="text-sm text-slate-600">{t('due')}: {new Date(task.dueDate).toLocaleDateString()}</p>
                    </div>
                    <Badge 
                      variant={task.priority === 'Urgent' ? 'destructive' : 'default'}
                    >
                      {task.priority}
                    </Badge>
                  </div>
                ))}
                {urgentTasks.length > 3 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => onNavigateToTab('tasks')}
                  >
                    {t('viewAllTasks')} ({urgentTasks.length})
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Recent Activity */}
      <Card className={getCardClasses('basic')}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className={`h-5 w-5 text-${colors.success.main}`} />
            <span>{t('recentActivity')}</span>
          </CardTitle>
          <CardDescription>
            {t('recentActivityDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthRecords.slice(0, 3).map((record) => {
              const animal = animals.find(a => a.id === record.animalId);
              return (
                <div key={record.id} className={cn(
                  "flex items-start space-x-3 p-3 rounded-lg",
                  getShadowClasses('level1').replace('hover:', ''),
                  "bg-slate-50"
                )}>
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Stethoscope className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">
                      {t('healthRecordAdded', { name: animal?.name })}
                    </p>
                    <p className="text-sm text-slate-600">
                      {record.type} - {record.diagnosis}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(record.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
            
            {urgentTasks.slice(0, 2).map((task) => (
              <div key={task.id} className={cn(
                "flex items-start space-x-3 p-3 rounded-lg",
                getShadowClasses('level1').replace('hover:', ''),
                "bg-slate-50"
              )}>
                <div className={`w-8 h-8 bg-${colors.success.bg} rounded-full flex items-center justify-center`}>
                  <ClipboardList className={`h-4 w-4 text-${colors.success.main}`} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {t('newTask')}: {task.title}
                  </p>
                  <p className="text-sm text-slate-600">
                    {t('assignedTo')} {task.assignedTo}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {t('due')}: {new Date(task.dueDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 